import { pb } from './pb';
import { ParsedFitResult, TelemetryData } from './fitParser';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface WellnessRecord {
  date: string;          // YYYY-MM-DD
  hrv: number | null;
  rhr: number | null;
  sleep_score: number | null;
  body_battery: number | null;
  hrv_baseline: number | null;
  stress_level: number | null;
  sleep_deep: number | null;   // minutes
  sleep_rem: number | null;    // minutes
  sleep_light: number | null;  // minutes
}

export interface ActivityMetric {
  date: string;
  vo2max: number | null;
  tss: number | null;
  sport: string;
  ef: number | null; // Efficiency Factor: NP/avgHR or pace/avgHR
}

// ─── Activities ───────────────────────────────────────────────────────────────

export async function fetchActivitiesFromPB(): Promise<ParsedFitResult[]> {
  try {
    const records = await pb.collection('activities').getFullList({
      sort: '-timestamp',
      requestKey: null,
      batch: 500,          // prevent silent truncation on large collections
    });

    const results: ParsedFitResult[] = [];

    for (const record of records) {
      try {
        // ── Timestamp: try multiple common field names ──────────────────────
        const rawTs =
          record.timestamp    ??
          record.start_time   ??
          record.started_at   ??
          record.date         ??
          null;

        const startTime = rawTs ? new Date(rawTs) : null;
        if (!startTime || isNaN(startTime.getTime())) {
          console.warn('[pbSync] skipping record with invalid timestamp:', record.id, rawTs);
          continue;
        }

        // ── Sport / activity type ───────────────────────────────────────────
        const rawType: string =
          record.type           ??
          record.activity_type  ??
          record.sport          ??
          'unknown';

        // Normalise Garmin "HIIT" → stored as-is; we label it in the UI
        const sport = rawType || 'unknown';

        // ── Duration ───────────────────────────────────────────────────────
        const totalDuration =
          record.duration       ??
          record.elapsed_time   ??
          record.moving_time    ??
          0;

        // ── Distance ───────────────────────────────────────────────────────
        const totalDistance =
          record.distance       ??
          record.total_distance ??
          0;

        // ── Telemetry streams ──────────────────────────────────────────────
        const telemetry: TelemetryData = {
          timestamps:  [],
          heart_rate:  record.hr_stream    || [],
          power:       record.power_stream || [],
          cadence:     record.cadence_stream || [],
          speed:       record.speed_stream || [],
          altitude:    record.alt_stream   || [],
          gps_lat:     [],
          gps_lng:     [],
        };

        if (record.gps_stream && Array.isArray(record.gps_stream)) {
          record.gps_stream.forEach((pt: any) => {
            telemetry.gps_lat.push(pt.lat);
            telemetry.gps_lng.push(pt.lng);
            // If alt_stream wasn't stored separately, pull per-point altitude
            if (!record.alt_stream?.length && pt.alt != null) {
              telemetry.altitude.push(pt.alt);
            }
          });
        }

        const streamLength = Math.max(
          telemetry.heart_rate.length,
          telemetry.power.length,
          telemetry.speed.length,
          telemetry.gps_lat.length,
        );

        if (streamLength > 0 && totalDuration) {
          const step = totalDuration / streamLength;
          for (let i = 0; i < streamLength; i++) {
            telemetry.timestamps.push(Math.round(i * step));
          }
        }

        // ── Physics-based Power Estimation for Cycling ──
        const isBike = sport.includes('cycl') || sport.includes('bik');
        const hasPower = telemetry.power.some(p => p > 0);
        let finalNormalizedPower = record.normalized_power || null;

        if (isBike && !hasPower && telemetry.speed.length > 0) {
          const m = 97.4; // 87.9kg rider + ~9.5kg CAAD8
          const g = 9.81;
          const Crr = 0.0058; // Continental Gatorskin 700c
          const CdA = 0.38; // Standard road posture
          const rho = 1.2;
          
          let sumPower = 0;
          let countPower = 0;
          telemetry.power = Array(telemetry.speed.length).fill(0);

          // Apply a slight low-pass filter to altitude to smooth out grade spikes
          const smoothedAlt = [...telemetry.altitude];
          for (let i = 2; i < smoothedAlt.length - 2; i++) {
            smoothedAlt[i] = (telemetry.altitude[i-2] + telemetry.altitude[i-1] + telemetry.altitude[i] + telemetry.altitude[i+1] + telemetry.altitude[i+2]) / 5;
          }

          for (let i = 0; i < telemetry.timestamps.length; i++) {
            const v = telemetry.speed[i]; // m/s
            if (v < 1) continue;

            let grade = 0;
            if (i > 0) {
              const dt = telemetry.timestamps[i] - telemetry.timestamps[i - 1];
              const dAlt = smoothedAlt[i] - smoothedAlt[i - 1];
              if (dt > 0 && Math.abs(dAlt) < 5) {
                const dist = v * dt;
                if (dist > 0) {
                  grade = dAlt / dist;
                }
              }
            }

            grade = Math.max(-0.2, Math.min(0.2, grade));

            const F_roll = Crr * m * g;
            const F_grav = m * g * grade;
            const F_aero = 0.5 * rho * CdA * v * v;
            
            const pwr = (F_roll + F_grav + F_aero) * v;
            let pwrRounded = pwr > 0 ? Math.round(pwr) : 0;
            
            // Coasting heuristic & artifact capping
            if (grade < -0.02 && v > 11) {
              pwrRounded = 0; // Likely coasting > 40kph downhill
            } else if (pwrRounded > 1000) {
              pwrRounded = 1000; // Cap noisy GPS altimeter spikes
            }
            
            telemetry.power[i] = pwrRounded > 0 ? Math.round(pwrRounded / 0.97) : 0;
            
            if (telemetry.power[i] > 0) {
              sumPower += telemetry.power[i];
              countPower++;
            }
          }
          
          if (countPower > 30) {
            let sum4 = 0;
            let validVals = 0;
            for (let i = 29; i < telemetry.power.length; i++) {
              let rollSum = 0;
              for (let j = 0; j < 30; j++) rollSum += telemetry.power[i - j];
              const rollAvg = rollSum / 30;
              sum4 += Math.pow(rollAvg, 4);
              validVals++;
            }
            if (validVals > 0) {
              finalNormalizedPower = Math.round(Math.pow(sum4 / validVals, 0.25));
            }
          } else if (countPower > 0) {
            finalNormalizedPower = Math.round(sumPower / countPower);
          }
        }

        // ── Heart rate ─────────────────────────────────────────────────────
        let avgHeartRate = record.avg_heart_rate || record.average_hr || 0;
        if (!avgHeartRate && telemetry.heart_rate.length > 0) {
          avgHeartRate = telemetry.heart_rate.reduce((a: number, b: number) => a + b, 0)
            / telemetry.heart_rate.length;
        }

        results.push({
          startTime,
          totalDuration,
          totalDistance,
          avgHeartRate,
          normalizedPower: finalNormalizedPower,
          sport,
          telemetry,
        });
      } catch (recordErr) {
        console.warn('[pbSync] skipping malformed activity record:', record?.id, recordErr);
      }
    }

    console.info(`[pbSync] fetched ${results.length} / ${records.length} activities`);
    return results;

  } catch (error) {
    console.error('Error fetching activities from PocketBase:', error);
    return [];
  }
}

// ─── Wellness ─────────────────────────────────────────────────────────────────

export async function fetchWellnessFromPB(): Promise<WellnessRecord[]> {
  try {
    const records = await pb.collection('wellness').getFullList({
      sort: 'date',
      requestKey: null,
    });

    return records.map((r: any) => ({
      date: (r.date ?? '').split('T')[0],
      hrv:           r.hrv           ?? null,
      rhr:           r.rhr           ?? null,
      sleep_score:   r.sleep_score   ?? null,
      body_battery:  r.body_battery  ?? null,
      hrv_baseline:  r.hrv_baseline  ?? null,
      stress_level:  r.stress_level  ?? null,
      sleep_deep:    r.sleep_deep    ?? null,
      sleep_rem:     r.sleep_rem     ?? null,
      sleep_light:   r.sleep_light   ?? null,
    }));
  } catch (error) {
    console.error('Error fetching wellness from PocketBase:', error);
    return [];
  }
}

// ─── Activity Metrics (VO2max / EF per session) ───────────────────────────────

export async function fetchActivityMetricsFromPB(): Promise<ActivityMetric[]> {
  try {
    const records = await pb.collection('activities').getFullList({
      sort: 'timestamp',
      fields: 'timestamp,type,vo2_max,tss,normalized_power,avg_heart_rate',
      requestKey: null,
    });

    return records
      .filter((r: any) => r.vo2_max || r.tss || r.normalized_power)
      .map((r: any) => {
        const np   = r.normalized_power ?? 0;
        const avgHr = r.avg_heart_rate ?? 0;
        const ef   = (np > 0 && avgHr > 0) ? +(np / avgHr).toFixed(3) : null;
        return {
          date:    (r.timestamp ?? '').split('T')[0],
          vo2max:  r.vo2_max    ?? null,
          tss:     r.tss        ?? null,
          sport:   r.type       ?? 'unknown',
          ef,
        };
      });
  } catch (error) {
    console.error('Error fetching activity metrics from PocketBase:', error);
    return [];
  }
}
